// The air between the eye and what it looks at: one extinction function for everything the client draws (observer-centric LOD, step 2).
//
// The light from a thing at distance L along a ray reaches the eye as T * (the thing) + (1 - T) * (the sky in that direction), T = exp(-tau), tau the optical depth
// of the air on the way. The air is the sky's (skyPhysics.ts): Rayleigh scattering with a scale height of 8 km, and the aerosol with 1.2 km, the world's
// zenith optical depths (the weather message's tau_total and aerosol_depth) divided by those heights are their extinction at the sea. What is new is that
// the aerosol swells with the humidity of the air it floats in (airmap.py on the server, the "air_map" message): the extinction at a point is the sea-level
// one times exp(-h / H) times g, g the growth the map holds for the height band and the place. The integral along the ray is closed form for the height part
// (a piece of the ray between two heights, in a band where g is constant, is a difference of two exponentials) and a sum over a few pieces for the map.
//
// Nothing here knows which way is up beyond the one thing the air itself has: its density falls with height above the sea (`uAirL2.z`, the sea's y). A ray
// is a position and a unit direction, whatever the eye does.
//
// Two ways to read it, one function each, same law:
//   airTransmit     local: one growth per band, the eye's (what three's fog chunk and the sea and the pools use, all within a kilometre or two of the eye);
//   airTransmitMap  the map: the growth read along the ray (the cloud march, whose rays run to the horizon and beyond).
// Both give T per channel. The caller mixes: colour * T + sky * (1 - T).

import * as THREE from 'three';
import { H_RAY, H_MIE } from './skyPhysics.ts';
import { planet } from './planet.ts';

/** The "air_map" message (simulation/src/weather.py airmap_message). */
export interface AirMapMessage {
  type: 'air_map'; t: number; tick: number; n: number; bands: number; domain_m: number; gmax: number; edges: number[];
  /** `bands` planes of n x n bytes, one after the other (band-major), rows by world z, columns by world x, the periodic domain centred on the island; a byte is ln(g) / ln(gmax) * 255. */
  planes: string;
}

/** The parts of the weather message the air reads. */
export interface AirWeather { aerosol_scale: number; aerosol_depth: number; tau_total: [number, number, number] }

const EASE_S = 1.5;                       // seconds: the weather the air follows eases as the sky's does (sky.ts)
const BANDS = 3;

const COMMON_GLSL = /* glsl */`
uniform vec4 uAirL0;   // extinction at the sea per metre: rayleigh r, g, b, aerosol (grey); all zero when the air is off
uniform vec4 uAirL1;   // the aerosol's growth by height band at the eye (local reading)
uniform vec4 uAirL2;   // the band edges (m above the sea) between bands 0|1 and 1|2, the sea's y, ln(gmax)
const float AIR_H_RAY = ${H_RAY.toFixed(1)};
const float AIR_H_AER = ${H_MIE.toFixed(1)};
vec3 airSrgbToLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c)); }
// the integral of exp(-h / H) along the part of a ray that runs from height ha to hb (dy: the ray's vertical slope, never zero) and lies inside [lo, hi]
float airSeg(float ha, float hb, float dy, float lo, float hi, float H) {
  float a = max(min(ha, hb), lo), b = min(max(ha, hb), hi);
  return b > a ? (H / abs(dy)) * (exp(-a / H) - exp(-b / H)) : 0.0;
}
float airAer(float ha, float hb, float dy, vec3 g) {
  return g.x * airSeg(ha, hb, dy, 0.0, uAirL2.x, AIR_H_AER) + g.y * airSeg(ha, hb, dy, uAirL2.x, uAirL2.y, AIR_H_AER) + g.z * airSeg(ha, hb, dy, uAirL2.y, 1e6, AIR_H_AER);
}
// the ray's length inside the air: a ray going down ends at the sea (there is no air under it), and its vertical slope is kept off zero
float airLen(float h0, inout float dy, float L) {
  dy = abs(dy) < 1e-4 ? (dy < 0.0 ? -1e-4 : 1e-4) : dy;
  return dy < 0.0 ? min(L, max(h0, 0.0) / -dy) : L;
}
vec3 airTransmit(vec3 ro, vec3 d, float L) {
  float h0 = ro.y - uAirL2.z, dy = d.y;
  L = airLen(h0, dy, L);
  float h1 = h0 + dy * L;
  vec3 tau = uAirL0.rgb * airSeg(h0, h1, dy, 0.0, 1e6, AIR_H_RAY) + vec3(uAirL0.w * airAer(h0, h1, dy, uAirL1.xyz));
  return exp(-tau);
}
`;

const MAP_GLSL = /* glsl */`
uniform sampler2D uAirMap;   // the air map: band b in channel b, the periodic domain centred on the island, linear
uniform float uAirDom;       // the domain's side (m)
uniform vec2 uOriginXZ;      // the floating origin O (x, z), m (origin.ts): the ray's position is counted from it, the map's domain from the world's
uniform vec4 uPlanet;        // (the planet's radius in m, 0: flat; planet.ts)
// The air along a ray that is straight in the data's flat frame, at heights above the curved ground below it: the ground falls away as s^2 (1 - dy^2) / 2R under a ray of length s (planet.ts),
// so the ray is at h0 + dy s + a s^2 above it, a = (1 - dy^2) / 2R. A piece of the ray is taken as straight between its two ends (airSeg), which a piece of a long ray is to a good measure.
float airSlope(float ha, float hb, float ds) {
  float k = (hb - ha) / max(ds, 1e-3);
  return abs(k) < 1e-4 ? (k < 0.0 ? -1e-4 : 1e-4) : k;
}
vec3 airTransmitMap(vec3 ro, vec3 d, float L) {
  float h0 = ro.y - uAirL2.z, dy = d.y;
  float a = uPlanet.x > 0.0 ? (1.0 - dy * dy) * (0.5 / uPlanet.x) : 0.0;
  dy = abs(dy) < 1e-4 ? (dy < 0.0 ? -1e-4 : 1e-4) : dy;
  if (dy < 0.0) {                                                        // a ray going down ends at the ground (where its height above the curved ground first reaches 0)
    float hg = max(h0, 0.0), disc = dy * dy - 4.0 * a * hg;
    if (disc >= 0.0) L = min(L, 2.0 * hg / (-dy + sqrt(disc)));
  }
  float hL = h0 + dy * L + a * L * L;
  vec3 tau = uAirL0.rgb * airSeg(h0, hL, airSlope(h0, hL, L), 0.0, 1e6, AIR_H_RAY);
  float ta = 0.0, s0 = 0.0, ha = h0;
  for (int i = 1; i <= 6; i++) {                                         // pieces of the ray, closer together near the eye
    float f = float(i) / 6.0;
    float s1 = L * f * f;
    float hb = h0 + dy * s1 + a * s1 * s1;
    vec3 pm = ro + d * (0.5 * (s0 + s1));
    vec3 g = exp(texture2D(uAirMap, (pm.xz + uOriginXZ) / uAirDom + 0.5).rgb * uAirL2.w);
    ta += airAer(ha, hb, airSlope(ha, hb, s1 - s0), g);
    s0 = s1; ha = hb;
  }
  return exp(-(tau + vec3(uAirL0.w * ta)));
}
`;

/** GLSL for the local reading (declares uAirL0..2): add `air.uniforms` to the material. */
export const AIR_GLSL = COMMON_GLSL;
/** GLSL for the map reading (needs AIR_GLSL before it): add `air.uniforms`, `air.mapUniforms` and `origin.uniforms`; the ray's origin `ro` is counted from O (origin.ts). */
export const AIR_MAP_GLSL = MAP_GLSL;

/** three's fog, extended: the fog chunks of the built-in materials (terrain, grass, trees, rocks) take the air in front of everything they draw (three's own fog stays for the underwater look,
 *  which it mixes after the colour space as it always did). The varying `vAirRel` is the vertex
 *  from the eye in world axes, whatever the vertex has been through (instancing, skinning), and the uniforms are the same typed arrays in every material (a typed array is not
 *  cloned with a material's uniforms, so one write per frame reaches them all). */
function installFog(air: Air): boolean {
  const chunks = THREE.ShaderChunk as unknown as Record<string, string>;
  if (chunks.colorspace_fragment.includes('AIR_FOG')) return true;
  // all or nothing: every chunk has to be the text this expects before one is touched (half a patch would declare uniforms nothing feeds)
  const edits: [string, string, string][] = [
    ['fog_pars_vertex', 'varying float vFogDepth;', 'varying float vFogDepth;\n\tvarying vec3 vAirRel;'],
    // (with the curved world, planet.ts, `mvFlat` is the vertex before it was bent: the air's model is the flat data's, and a point on the curved ground is at its flat height and distance in it)
    ['fog_vertex', 'vFogDepth = - mvPosition.z;', `vFogDepth = - mvPosition.z;\n\tvAirRel = transpose( mat3( viewMatrix ) ) * ${planet.installed ? 'mvFlat' : 'mvPosition.xyz'};`],
    ['fog_pars_fragment', 'varying float vFogDepth;', `varying float vFogDepth;\n\tvarying vec3 vAirRel;\n${COMMON_GLSL}`],
    ['colorspace_fragment', 'gl_FragColor = linearToOutputTexel( gl_FragColor );', `// AIR_FOG: the air in front of the surface (air.ts), on the linear colour after the tone curve, as the sky it fades to is drawn
	#ifdef USE_FOG
	{
		float airL = length( vAirRel );
		vec3 airT = airTransmit( cameraPosition, vAirRel / max( airL, 1e-3 ), airL );
		// three hands fogColor over in the output colour space (it mixes after the encoding); the air mixes before it, in linear
		vec3 airSky = abs( linearToOutputTexel( vec4( 0.5 ) ).r - 0.5 ) < 1e-3 ? fogColor : airSrgbToLinear( fogColor );
		gl_FragColor.rgb = gl_FragColor.rgb * airT + airSky * ( 1.0 - airT );
	}
	#endif
	gl_FragColor = linearToOutputTexel( gl_FragColor );`],
  ];
  if (!edits.every(([name, find]) => typeof chunks[name] === 'string' && chunks[name].includes(find))) return false;
  for (const [name, find, put] of edits) chunks[name] = chunks[name].replace(find, () => put);
  const lib = THREE.ShaderLib as unknown as Record<string, { uniforms: Record<string, { value: unknown }> }>;
  for (const k of Object.keys(lib)) {
    const u = lib[k].uniforms;
    if (u && 'fogColor' in u) {
      u.uAirL0 = { value: air.l0 }; u.uAirL1 = { value: air.l1 }; u.uAirL2 = { value: air.l2 };
    }
  }
  return true;
}

export class Air {
  /** The uniforms every reading needs (see AIR_GLSL); their values are shared typed arrays, written by `update`. */
  readonly l0 = new Float32Array(4);
  readonly l1 = new Float32Array(4);
  readonly l2 = new Float32Array(4);
  readonly uniforms = { uAirL0: { value: this.l0 }, uAirL1: { value: this.l1 }, uAirL2: { value: this.l2 } };
  /** Extra uniforms for the map reading (AIR_MAP_GLSL). */
  readonly mapUniforms: { uAirMap: { value: THREE.DataTexture }; uAirDom: { value: number } };
  /** Multiplies the extinction (1: the air as the world has it). */
  strength = 1;
  enabled = true;
  private tex: THREE.DataTexture;
  private texData = new Uint8Array(4);                   // the texture's own bytes (kept here: three's typings of `image.data` differ between versions)
  private n = 1;
  private bytes = new Uint8Array(BANDS);                     // band-major planes, n x n each, as the message sent them
  private lnGmax = Math.log(16);
  private edges: [number, number] = [500, 1500];
  private domain = 512000;
  private tauRay: [number, number, number] = [0.05, 0.10, 0.22];
  private tauAer = 0.05;
  private tgtRay: [number, number, number] = [0.05, 0.10, 0.22];
  private tgtAer = 0.05;
  private fresh = true;
  private installed = false;
  /** The last map's time stamp, 0 before one. */
  mapT = 0;

  constructor() {
    this.tex = new THREE.DataTexture(this.texData, 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.tex.wrapS = this.tex.wrapT = THREE.RepeatWrapping;
    this.tex.magFilter = this.tex.minFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;
    this.tex.needsUpdate = true;
    this.mapUniforms = { uAirMap: { value: this.tex }, uAirDom: { value: this.domain } };
    this.l2[3] = this.lnGmax;
  }

  /** Replace three's fog chunks (call once, before the first material compiles). False when the chunks are not what this expects: the fog is then three's own. */
  install(): boolean {
    if (!this.installed) this.installed = installFog(this);
    return this.installed;
  }

  /** The weather message: the zenith optical depths the extinction at the sea comes from. Eased in `update`. */
  setWeather(w: AirWeather): void {
    const ok = Array.isArray(w.tau_total) && w.tau_total.length >= 3 && [...w.tau_total, w.aerosol_depth, w.aerosol_scale].every(Number.isFinite);
    if (!ok) { console.warn('[sim] air: weather message without usable optical depths; the air keeps what it had'); return; }
    this.tgtRay = [w.tau_total[0] - w.aerosol_depth, w.tau_total[1] - w.aerosol_depth, w.tau_total[2] - w.aerosol_depth];
    this.tgtAer = w.aerosol_depth * w.aerosol_scale;
    if (this.fresh) { this.tauRay = [...this.tgtRay]; this.tauAer = this.tgtAer; this.fresh = false; }
  }

  /** An "air_map" message. */
  ingestMap(m: AirMapMessage): void {
    const n = m.n | 0, nb = Math.min(m.bands | 0, BANDS);
    const raw = atob(m.planes);
    if (n < 2 || raw.length < nb * n * n) throw new Error(`air_map: ${raw.length} bytes for ${nb} bands of ${n} x ${n}`);
    const plane = n * n;
    const bytes = new Uint8Array(BANDS * plane);
    for (let i = 0; i < nb * plane; i++) bytes[i] = raw.charCodeAt(i);
    const rgba = new Uint8Array(plane * 4);
    for (let i = 0; i < plane; i++) {
      for (let b = 0; b < BANDS; b++) rgba[i * 4 + b] = bytes[Math.min(b, nb - 1) * plane + i];
      rgba[i * 4 + 3] = 255;
    }
    if (n !== this.n) {
      this.tex.dispose();
      this.texData = rgba;
      this.tex = new THREE.DataTexture(this.texData, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
      this.tex.wrapS = this.tex.wrapT = THREE.RepeatWrapping;
      this.tex.magFilter = this.tex.minFilter = THREE.LinearFilter;
      this.tex.generateMipmaps = false;
      this.mapUniforms.uAirMap.value = this.tex;
    } else {
      this.texData.set(rgba);
    }
    this.tex.needsUpdate = true;
    this.n = n; this.bytes = bytes; this.domain = m.domain_m; this.lnGmax = Math.log(m.gmax);
    this.edges = [m.edges[1] ?? 500, m.edges[2] ?? 1500];
    this.mapUniforms.uAirDom.value = this.domain;
    this.mapT = m.t;
  }

  /** The map's growth by band at a place (world x, z): the bilinear reading the shaders make, on the host. */
  growthAt(x: number, z: number, out: [number, number, number] = [1, 1, 1]): [number, number, number] {
    const n = this.n, plane = n * n;
    if (n < 2) { out[0] = out[1] = out[2] = 1; return out; }
    const fx = (x / this.domain + 0.5) * n - 0.5, fz = (z / this.domain + 0.5) * n - 0.5;
    const ix = Math.floor(fx), iz = Math.floor(fz), tx = fx - ix, tz = fz - iz;
    const w = (v: number) => ((v % n) + n) % n;
    const x0 = w(ix), x1 = w(ix + 1), z0 = w(iz), z1 = w(iz + 1);
    for (let b = 0; b < BANDS; b++) {
      const o = b * plane;
      const v = (this.bytes[o + z0 * n + x0] * (1 - tx) + this.bytes[o + z0 * n + x1] * tx) * (1 - tz)
              + (this.bytes[o + z1 * n + x0] * (1 - tx) + this.bytes[o + z1 * n + x1] * tx) * tz;
      out[b] = Math.exp(v / 255 * this.lnGmax);
    }
    return out;
  }

  private readonly _g: [number, number, number] = [1, 1, 1];

  /** Per frame: ease the weather, read the map where the eye is, write the shared uniforms. `uw` is 0 in air and 1 under water (the air is off then: the underwater look is the renderer's). */
  update(dt: number, eye: THREE.Vector3, seaY: number, uw: number): void {
    const k = Number.isFinite(dt) ? Math.min(1, Math.max(0, dt) / EASE_S) : 1;
    for (let i = 0; i < 3; i++) this.tauRay[i] += (this.tgtRay[i] - this.tauRay[i]) * k;
    this.tauAer += (this.tgtAer - this.tauAer) * k;
    if (!Number.isFinite(eye.x + eye.y + eye.z + seaY)) { this.l0.fill(0); return; }
    const on = this.enabled ? this.strength * (1 - Math.min(1, Math.max(0, uw))) : 0;
    const l0 = this.l0, g = this.growthAt(eye.x, eye.z, this._g);
    l0[0] = on * this.tauRay[0] / H_RAY; l0[1] = on * this.tauRay[1] / H_RAY; l0[2] = on * this.tauRay[2] / H_RAY;
    l0[3] = on * this.tauAer / H_MIE;
    this.l1[0] = g[0]; this.l1[1] = g[1]; this.l1[2] = g[2]; this.l1[3] = 0;
    this.l2[0] = this.edges[0]; this.l2[1] = this.edges[1]; this.l2[2] = seaY; this.l2[3] = this.lnGmax;
  }
}

/** The one air (the fog chunks are global, so is it). */
export const air = new Air();
